// Core Imports
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'
import { queryStaticList } from '@/core/lib/static-list'

// Module Imports
import {
  INDIVIDUAL_ACHIEVEMENTS_SEED,
  earnersForAchievement,
} from '@/modules/individual-achievement/individual-achievement.data'
import type {
  TCreateIndividualAchievementRequest,
  TEditIndividualAchievementRequest,
  TIndividualAchievement,
  TIndividualAchievementDetail,
  TToggleIndividualAchievementActiveRequest,
} from '@/modules/individual-achievement/individual-achievement.types'

// In-memory store (clone of the seed) so create / edit / toggle / delete feel
// live in the UI until the real endpoints land. Swap the bodies below for
// `api.*` calls against `individualAchievementEndpoints` — signatures already match.
let store: TIndividualAchievement[] = INDIVIDUAL_ACHIEVEMENTS_SEED.map((a) => ({
  ...a,
}))

let seq = 0
const nextId = () => {
  seq += 1
  return `custom-${seq}`
}

export async function getIndividualAchievementList(
  params: TAdminListParams
): Promise<TResponse<TIndividualAchievement[]>> {
  return queryStaticList(store, params)
}

export async function getIndividualAchievementDetail(
  id: string
): Promise<TIndividualAchievementDetail> {
  const found = store.find((a) => a.id === id)
  if (!found) throw new Error('INDIVIDUAL_ACHIEVEMENT_NOT_FOUND')
  return { ...found, earners: earnersForAchievement(id) }
}

export async function createIndividualAchievement(
  payload: TCreateIndividualAchievementRequest
): Promise<void> {
  const image = URL.createObjectURL(payload.file)
  store = [
    {
      id: nextId(),
      name: payload.name,
      description: payload.description,
      image,
      tier: 'gold',
      unlock_rules: payload.unlock_rules,
      acquired_at: null,
      is_active: true,
    },
    ...store,
  ]
}

export async function editIndividualAchievement(
  payload: TEditIndividualAchievementRequest
): Promise<void> {
  store = store.map((a) =>
    a.id === payload.id
      ? {
          ...a,
          name: payload.name ?? a.name,
          description: payload.description ?? a.description,
          image: payload.file ? URL.createObjectURL(payload.file) : a.image,
          unlock_rules: payload.unlock_rules ?? a.unlock_rules,
        }
      : a
  )
}

export async function toggleIndividualAchievementActive(
  payload: TToggleIndividualAchievementActiveRequest
): Promise<TIndividualAchievement> {
  store = store.map((a) =>
    a.id === payload.id ? { ...a, is_active: payload.is_active } : a
  )
  const updated = store.find((a) => a.id === payload.id)
  if (!updated) throw new Error('INDIVIDUAL_ACHIEVEMENT_NOT_FOUND')
  return updated
}

export async function deleteIndividualAchievement(id: string): Promise<void> {
  store = store.filter((a) => a.id !== id)
}
